// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

export function registerServiceWorker(config?: Config): void {
  console.error('[Service Worker] registerServiceWorker called. isLocalhost:', isLocalhost, 'NODE_ENV:', process.env.NODE_ENV);

  if ('serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      console.error('[Service Worker] Registration skipped — PUBLIC_URL origin mismatch');
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
      console.error('[Service Worker] Window loaded. SW URL:', swUrl);

      if (isLocalhost) {
        console.error('[Service Worker] Running on localhost — validating SW before registering');
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.error(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://cra.link/PWA'
          );
        });
      } else {
        // Is not localhost. Just register service worker
        console.error('[Service Worker] Not localhost — registering directly');
        registerValidSW(swUrl, config);
      }
    });
  } else {
    console.error('[Service Worker] Not supported in this browser');
  }
}

// Guard: only allow one SW-triggered reload per session to prevent loops.
let hasReloadedForSWUpdate = false;

function registerValidSW(swUrl: string, config?: Config): void {
  console.error('[Service Worker] Registering SW from:', swUrl);

  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      console.error('[Service Worker] Registration successful. Scope:', registration.scope);
      console.error('[Service Worker] Active SW:', registration.active?.state ?? 'none');
      console.error('[Service Worker] Waiting SW:', registration.waiting?.state ?? 'none');
      console.error('[Service Worker] Installing SW:', registration.installing?.state ?? 'none');
      console.error('[Service Worker] Currently controlled by:', navigator.serviceWorker.controller?.state ?? 'no controller');

      // Check for SW updates every 4 hours for long-lived tabs
      setInterval(() => {
        registration.update().catch((err) => {
          console.log('[Service Worker] Update check failed:', err);
        });
      }, 4 * 60 * 60 * 1000);

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          console.log('[Service Worker] onupdatefound fired but installing is null');
          return;
        }
        console.log('[Service Worker] Update found — new worker is installing. State:', installingWorker.state);

        installingWorker.onstatechange = () => {
          console.log('[Service Worker] Installing worker state changed to:', installingWorker.state);

          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              console.log('[Service Worker] New SW installed while old one still controls page — update pending');

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              console.log('[Service Worker] SW installed for the first time — content cached for offline use');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }

          if (installingWorker.state === 'redundant') {
            console.warn('[Service Worker] Installing worker became redundant (failed to install or was superseded)');
          }
        };
      };

      // When a new SW activates and calls clients.claim(), this event fires.
      // Reload once so the page is served by the new SW, but guard against loops.
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('[Service Worker] controllerchange fired. hasReloadedForSWUpdate:', hasReloadedForSWUpdate);
        console.log('[Service Worker] New controller state:', navigator.serviceWorker.controller?.state ?? 'null');
        if (hasReloadedForSWUpdate) {
          console.warn('[Service Worker] controllerchange fired again — skipping reload to prevent loop');
          return;
        }
        hasReloadedForSWUpdate = true;
        console.log('[Service Worker] >>> RELOADING PAGE due to controllerchange <<<');
        window.location.reload();
      });
    })
    .catch((error) => {
      console.error('[Service Worker] Registration failed:', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config): void {
  console.error('[Service Worker] Checking validity of SW at:', swUrl);
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      console.log('[Service Worker] SW fetch response — status:', response.status, 'content-type:', contentType);
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        console.warn('[Service Worker] SW not found or wrong content-type — unregistering');
        // No service worker found. Probably a different app. Unregister and reload once.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            if (!hasReloadedForSWUpdate) {
              hasReloadedForSWUpdate = true;
              console.log('[Service Worker] >>> RELOADING PAGE after unregistering invalid SW <<<');
              window.location.reload();
            } else {
              console.warn('[Service Worker] Skipping reload after unregister — already reloaded this session');
            }
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        console.log('[Service Worker] SW validated — proceeding to register');
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('[Service Worker] Fetch failed — no internet connection. App is running in offline mode.');
    });
}

export function unregisterServiceWorker(): void {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
} 